import type { FileMetaDataConfigDto, FileMetaDataFieldConfigDto } from '@/api/ApiClient';

export type InstrumentMetadataKind = 'characterization' | 'synthesis';

/** Customizing / new instruments: manual entry only until filename parsing is re-enabled in the UI. */
export const INSTRUMENT_METADATA_SOURCE_MANUAL = 'Manual';

export function sortMetadataConfigFields(fields: FileMetaDataFieldConfigDto[]): FileMetaDataFieldConfigDto[] {
  return [...fields].sort((a, b) => {
    const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return (a.label ?? '').localeCompare(b.label ?? '', undefined, { sensitivity: 'base' });
  });
}

/** True when the instrument row has configured metadata fields to show in a grid detail panel. */
export function hasInstrumentFileMetadataContent(config: FileMetaDataConfigDto | undefined): boolean {
  return (config?.fields ?? []).some((f) => Boolean(f.label?.trim()));
}

/** Empty system-wide config for a new or edited instrument in customizing. */
export function createEmptyInstrumentMetadataConfig(
  kind: InstrumentMetadataKind,
  instrumentId: string
): FileMetaDataConfigDto {
  return {
    isDefault: true,
    charInstrument: kind === 'characterization' ? instrumentId : undefined,
    synInstrument: kind === 'synthesis' ? instrumentId : undefined,
    metadataSource: INSTRUMENT_METADATA_SOURCE_MANUAL,
    filenameTemplate: '',
    fields: [],
  };
}

/** Keep instrument id on the config DTO when the customizing form id field changes. */
export function syncMetadataConfigInstrumentId(
  config: FileMetaDataConfigDto | undefined,
  kind: InstrumentMetadataKind,
  instrumentId: string
): FileMetaDataConfigDto {
  const base = config ?? createEmptyInstrumentMetadataConfig(kind, instrumentId);
  return {
    ...base,
    charInstrument: kind === 'characterization' ? instrumentId : undefined,
    synInstrument: kind === 'synthesis' ? instrumentId : undefined,
  };
}

/** Strip UI-only test results before POSTing with characterization/synthesis instrument save. */
export function normalizeMetadataConfigForInstrumentSave(
  config: FileMetaDataConfigDto | undefined,
  kind: InstrumentMetadataKind,
  instrumentId: string
): FileMetaDataConfigDto | undefined {
  if (!config) {
    return undefined;
  }
  const fields = config.fields
    ?.map((field, index) => {
      const row: FileMetaDataFieldConfigDto = {
        id: field.id,
        label: field.label,
        type: field.type,
        order: field.order ?? index,
        templatePattern: field.templatePattern,
      };
      return row;
    })
    .filter((f) => f.label?.trim());

  const hasFields = Boolean(fields?.length);

  if (!hasFields) {
    return undefined;
  }

  return {
    id: config.id,
    isDefault: true,
    charInstrument: kind === 'characterization' ? instrumentId : undefined,
    synInstrument: kind === 'synthesis' ? instrumentId : undefined,
    metadataSource: INSTRUMENT_METADATA_SOURCE_MANUAL,
    fields,
  };
}
